import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { logError } from "../lib/logger";
import { FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("cindy_chat_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("cindy_chat_session", id);
  }
  return id;
};

const WELCOME = {
  role: "assistant",
  text: "Hi! 👋 I'm Cindy, your virtual assistant. Ask me anything about our cleaning services in Naples, FL.\n\n¡Hola! 👋 Soy Cindy, tu asistente virtual. Pregúntame sobre nuestros servicios de limpieza en Naples, FL.",
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const renderWithLinks = (text) =>
  text.split(URL_REGEX).map((part, i) => {
    const key = `${i}-${part.slice(0, 24)}`;
    return part.startsWith("http") ? (
      <a key={key} href={part} target="_blank" rel="noopener noreferrer" className="underline font-bold" style={{ color: "inherit" }}>
        {part}
      </a>
    ) : (
      <React.Fragment key={key}>{part}</React.Fragment>
    );
  });

const ChatBubble = ({ role, text }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={isUser ? "chat-message-user" : "chat-message-bot"}
        className="max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
        style={{
          background: isUser ? NAVY : "#F0F4FF",
          color: isUser ? "#FFFFFF" : "#2D2D2D",
          fontFamily: FONT_BODY,
          borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        }}
      >
        {renderWithLinks(text)}
      </div>
    </div>
  );
};

const TypingDots = () => (
  <div className="flex justify-start" data-testid="chat-typing">
    <div className="px-4 py-3 flex gap-1.5" style={{ background: "#F0F4FF", borderRadius: "14px 14px 14px 4px" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={`dot-${i}`}
          className="w-2 h-2 rounded-full animate-bounce"
          style={{ background: NAVY, animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  </div>
);

const ChatHeader = ({ onClose }) => (
  <div className="flex items-center justify-between px-4 py-3" style={{ background: NAVY }}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(212,168,67,0.25)", border: `2px solid ${GOLD}` }}>
        <Sparkles size={16} style={{ color: GOLD }} />
      </div>
      <div>
        <div className="text-sm font-bold text-white" style={{ fontFamily: FONT_HEAD }}>
          Cindy — Virtual Assistant
        </div>
        <div className="text-xs flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}>
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#4ade80" }} />
          Online · English / Español
        </div>
      </div>
    </div>
    <button data-testid="chat-close-btn" onClick={onClose} className="p-1.5 text-white hover:opacity-70 transition-opacity" aria-label="Close chat">
      <X size={18} />
    </button>
  </div>
);

const readSseStream = async (res, onText) => {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = JSON.parse(line.slice(6));
      if (payload.error) throw new Error(payload.error);
      if (payload.delta) {
        fullText += payload.delta;
        onText(fullText);
      }
    }
  }
  return fullText;
};

const ERROR_REPLY =
  "Sorry, something went wrong. Please call us at (239) 244-1934. / Lo siento, algo salió mal. Llámanos al (239) 244-1934.";

const makeStreamRenderer = (setMessages, setLoading) => {
  let started = false;
  return (botText) => {
    if (!started) {
      started = true;
      setLoading(false);
      setMessages((prev) => [...prev, { role: "assistant", text: botText }]);
    } else {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", text: botText };
        return next;
      });
    }
  };
};

const fetchHistory = async () => {
  try {
    const res = await fetch(`${API}/chat/history/${getSessionId()}`);
    const data = await res.json();
    if (!data.messages?.length) return null;
    return [WELCOME, ...data.messages.map((m) => ({ role: m.role, text: m.text }))];
  } catch (err) {
    logError("Chat history load failed", err);
    return null;
  }
};

const streamReply = async (text, setMessages, setLoading) => {
  setMessages((prev) => [...prev, { role: "user", text }]);
  setLoading(true);
  const renderStreamText = makeStreamRenderer(setMessages, setLoading);
  try {
    const res = await fetch(`${API}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: getSessionId(), message: text }),
    });
    const fullText = await readSseStream(res, renderStreamText);
    if (!fullText) throw new Error("empty response");
  } catch (err) {
    logError("Chat send failed", err);
    setMessages((prev) => [...prev, { role: "assistant", text: ERROR_REPLY }]);
  } finally {
    setLoading(false);
  }
};

const useChat = () => {
  const [messages, setMessages] = useState([WELCOME]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  const loadHistory = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchHistory().then((history) => history && setMessages(history));
  }, [loadedRef, setMessages]);

  const sendMessage = useCallback((text) => streamReply(text, setMessages, setLoading), [setMessages, setLoading]);

  return { messages, loading, loadHistory, sendMessage };
};

const ChatMessages = ({ messages, loading, bottomRef }) => (
  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "#FAFBFF" }}>
    {messages.map((m, i) => (
      <ChatBubble key={`msg-${i}-${m.role}`} role={m.role} text={m.text} />
    ))}
    {loading && <TypingDots />}
    <div ref={bottomRef} />
  </div>
);

const ChatComposer = ({ input, setInput, loading, onSend }) => (
  <form onSubmit={onSend} className="flex items-center gap-2 p-3 border-t" style={{ borderColor: "rgba(27,58,107,0.1)", background: "#FFFFFF" }}>
    <input
      type="text"
      value={input}
      onChange={(e) => setInput(e.target.value)}
      placeholder="Type a message… / Escribe un mensaje…"
      data-testid="chat-input"
      className="flex-1 px-4 py-2.5 text-sm outline-none"
      style={{
        fontFamily: FONT_BODY,
        border: "1px solid rgba(27,58,107,0.2)",
        borderRadius: "24px",
        color: "#2D2D2D",
      }}
    />
    <button
      type="submit"
      data-testid="chat-send-btn"
      disabled={loading || !input.trim()}
      className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-90 disabled:opacity-40"
      style={{ background: NAVY }}
      aria-label="Send message"
    >
      <Send size={16} style={{ color: "#FFFFFF" }} />
    </button>
  </form>
);

const ChatLauncher = ({ onOpen }) => (
  <button
    data-testid="chat-widget-button"
    onClick={onOpen}
    className="fixed z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-transform hover:scale-110 bottom-20 right-5 md:bottom-6 md:right-6"
    style={{ background: GOLD, border: `2px solid ${NAVY}` }}
    aria-label="Open chat"
  >
    <MessageCircle size={24} style={{ color: NAVY }} />
  </button>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const { messages, loading, loadHistory, sendMessage } = useChat();
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, bottomRef]);

  const handleSend = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    sendMessage(text);
  };

  return (
    <>
      {!open && <ChatLauncher onOpen={() => setOpen(true)} />}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-50 flex flex-col overflow-hidden shadow-2xl bottom-0 right-0 left-0 md:left-auto md:bottom-6 md:right-6 md:w-[380px] rounded-t-2xl md:rounded-2xl"
          style={{ height: "min(540px, 85vh)", background: "#FFFFFF", border: "1px solid rgba(27,58,107,0.15)" }}
        >
          <ChatHeader onClose={() => setOpen(false)} />
          <ChatMessages messages={messages} loading={loading} bottomRef={bottomRef} />
          <ChatComposer input={input} setInput={setInput} loading={loading} onSend={handleSend} />
        </div>
      )}
    </>
  );
};
